import { PayloadAction, createSlice } from "@reduxjs/toolkit";

import { RootState } from "../store";
import { useSelector } from "react-redux";

export type State = {
  email: string;
  name: string;
  picture: string | null;
  credentials: string;
  authority: string;
  token: string;
  expires: string;
  patientGroups?: string[];
  whatsAppVoiceCallEnabledGroups?: string[];
  patientGroup?: { [key: string]: string };
  patientSubGroup?: { [key: string]: string };
  termsOfUseAcceptance?: { [key: string]: boolean };
};

const authSlice = createSlice({
  name: "auth",
  initialState: {
    email: "",
    name: "",
    picture: null,
    credentials: "",
    authority: "",
    token: "",
    expires: "",
    patientGroups: undefined,
    whatsAppVoiceCallEnabledGroups: undefined,
    patientGroup: undefined,
    patientSubGroup: undefined,
    termsOfUseAcceptance: undefined,
  },
  reducers: {
    setCredentials: (state: State, action: PayloadAction<State>) => {
      state.email = action.payload.email;
      state.name = action.payload.name;
      state.picture = action.payload.picture;
      state.credentials = action.payload.credentials;
      state.authority = action.payload.authority;
      state.token = action.payload.token;
      state.expires = action.payload.expires;
      state.patientGroups = action.payload.patientGroups;
      state.patientGroup = action.payload.patientGroup;
    },
    setPatientGroup: (state: State, action: PayloadAction<string>) => {
      state.patientGroup = Object.assign(state.patientGroup || {}, {
        [state.email]: action.payload || state.patientGroup?.[state.email],
      });
    },
    setPatientSubGroup: (state: State, action: PayloadAction<string>) => {
      const newSubGroup =
        action.payload === ""
          ? ""
          : action.payload || state.patientSubGroup?.[state.email];
      state.patientSubGroup = Object.assign(state.patientSubGroup || {}, {
        [state.email]: newSubGroup,
      });
    },
    setPatientGroups: (state: State, action: PayloadAction<string[]>) => {
      state.patientGroups = action.payload;
    },
    setWhatsAppVoiceCallEnabledGroups: (
      state: State,
      action: PayloadAction<string[]>
    ) => {
      state.whatsAppVoiceCallEnabledGroups = action.payload;
    },
    setTermsOfUseAcceptance: (state: State, action: PayloadAction<boolean>) => {
      state.termsOfUseAcceptance = Object.assign(
        state.termsOfUseAcceptance || {},
        {
          [state.email]: action.payload,
        }
      );
    },
    logOut: (state: State, action: PayloadAction<void>) => {
      state.email = "";
      state.name = "";
      state.picture = null;
      state.credentials = "";
      state.authority = "";
      state.token = "";
      state.expires = "";
      state.patientGroups = undefined;
      state.whatsAppVoiceCallEnabledGroups = undefined;
    },
  },
});

export const getPatientGroup = (state: State) => {
  return state.patientGroup?.[state.email] || "unknown";
};

export const getPatientSubGroup = (state: State) => {
  return state.patientSubGroup?.[state.email] || undefined;
};

export const {
  setCredentials,
  setPatientGroup,
  setPatientSubGroup,
  setPatientGroups,
  setWhatsAppVoiceCallEnabledGroups,
  setTermsOfUseAcceptance,
  logOut,
} = authSlice.actions;

export default authSlice.reducer;

export const selectCurrentEmail = (state: RootState) => state.auth.email;
export const selectCurrentName = (state: RootState) => state.auth.name;
export const selectCurrentPicture = (state: RootState): string | null =>
  state.auth.picture;
export const selectCurrentToken = (state: RootState) => state.auth.token;
export const selectCurrentPatientGroups = (
  state: RootState
): string[] | undefined => state.auth.patientGroups;
export const selectCurrentPatientGroup = (
  state: RootState
): { [key: string]: string } | undefined => state.auth.patientGroup;
export const selectCurrentPatientSubGroup = (
  state: RootState
): { [key: string]: string } | undefined => state.auth.patientSubGroup;

const selectCurrentTermsOfUseAcceptance = (
  state: RootState
): { [key: string]: boolean } | undefined => state.auth.termsOfUseAcceptance;
export const useCurrentTermsOfUseAcceptance = () => {
  const userEmail = useSelector(selectCurrentEmail);
  return useSelector(selectCurrentTermsOfUseAcceptance)?.[userEmail] || false;
};

const selectWhatsAppVoiceCallEnabledGroups = (
  state: RootState
): string[] | undefined => state.auth.whatsAppVoiceCallEnabledGroups;
export const selectCurrentWhatsAppVoiceCallEnabled = (
  state: RootState
): boolean =>
  selectWhatsAppVoiceCallEnabledGroups(state)?.includes(
    getPatientGroup(state.auth)
  ) || false;
export const useCurrentWhatsAppVoiceCallEnabled = () =>
  useSelector(selectCurrentWhatsAppVoiceCallEnabled);
